import {createStyles} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';

import {PDF_MIME_TYPE} from '../FilePreviewConstants';

const useStyles = createStyles(() => ({
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
}));

interface PdfRendererProps {
  url: string;
  title?: string;
}

export default function PdfRenderer({url, title = 'PDF preview'}: PdfRendererProps): JSX.Element {
  const {classes} = useStyles();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let currentObjectUrl: string | null = null;

    setObjectUrl(null);
    setError(false);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load PDF preview.');
        }
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) {
          return;
        }
        currentObjectUrl = window.URL.createObjectURL(new Blob([blob], {type: PDF_MIME_TYPE}));
        setObjectUrl(currentObjectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      });

    return () => {
      cancelled = true;
      if (currentObjectUrl != null) {
        window.URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [url]);

  if (error) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load PDF preview." />;
  }
  if (objectUrl == null) {
    return <CenteredSpinner />;
  }
  return <iframe src={objectUrl} title={title} className={classes.iframe} />;
}
